import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup, Button, Form, Row, Col } from 'react-bootstrap';

const STORAGE_KEY = 'whatsappAnalyticsRange';
const IST_TIMEZONE = 'Asia/Kolkata';
const MAX_RANGE_DAYS = 366;
const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const PRESETS = [
    { days: 7, label: '7 days' },
    { days: 30, label: '30 days' },
    { days: 90, label: '90 days' }
];

const DEFAULT_DAYS = 30;

const parseUtcDay = (day) => {
    const [year, month, date] = day.split('-').map(Number);
    return Date.UTC(year, month - 1, date);
};

const addDays = (day, offset) => new Date(parseUtcDay(day) + offset * DAY_MS).toISOString().slice(0, 10);

export const istToday = () => new Date().toLocaleDateString('en-CA', { timeZone: IST_TIMEZONE });

export const presetRange = (days) => {
    const to = istToday();
    return { from: addDays(to, -(days - 1)), to, preset: days };
};

export const rangeDays = ({ from, to }) => Math.round((parseUtcDay(to) - parseUtcDay(from)) / DAY_MS) + 1;

export const validateRange = ({ from, to }) => {
    if (!DAY_PATTERN.test(from || '') || !DAY_PATTERN.test(to || '')) return 'Pick both dates';
    const days = rangeDays({ from, to });
    if (days < 1) return 'From date must not be after the to date';
    if (days > MAX_RANGE_DAYS) return `Range can be at most ${MAX_RANGE_DAYS} days`;
    return null;
};

const readStoredRange = () => {
    try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
        if (stored && !validateRange(stored)) return stored;
    } catch (err) {
        return null;
    }
    return null;
};

const writeStoredRange = (range) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(range));
    } catch (err) {
        // storage can be blocked; the range then lasts for this page only
    }
};

// `requested` is an optional { from, to } (for example from a link query string); when valid it replaces the stored range
export const useDateRange = (requested) => {
    const [range, setRangeState] = useState(() => {
        if (requested && !validateRange(requested)) {
            const linked = { from: requested.from, to: requested.to, preset: null };
            writeStoredRange(linked);
            return linked;
        }
        return readStoredRange() || presetRange(DEFAULT_DAYS);
    });

    const setRange = useCallback((next) => {
        writeStoredRange(next);
        setRangeState(next);
    }, []);

    return { range, setRange };
};

const DateRangeBar = ({ range, onChange }) => {
    const [custom, setCustom] = useState({ from: range.from, to: range.to });
    const [customError, setCustomError] = useState(null);
    const [customOpen, setCustomOpen] = useState(!range.preset);

    const selectPreset = (days) => {
        const next = presetRange(days);
        setCustom({ from: next.from, to: next.to });
        setCustomError(null);
        setCustomOpen(false);
        onChange(next);
    };

    const applyCustom = (event) => {
        event.preventDefault();
        const message = validateRange(custom);
        setCustomError(message);
        if (!message) onChange({ from: custom.from, to: custom.to, preset: null });
    };

    return (
        <div className="d-flex flex-wrap align-items-start gap-3">
            <ButtonGroup aria-label="Date range">
                {PRESETS.map(({ days, label }) => (
                    <Button
                        key={days}
                        variant={range.preset === days && !customOpen ? 'primary' : 'outline-primary'}
                        onClick={() => selectPreset(days)}
                    >
                        {label}
                    </Button>
                ))}
                <Button
                    variant={customOpen ? 'primary' : 'outline-primary'}
                    onClick={() => setCustomOpen(true)}
                >
                    Custom
                </Button>
            </ButtonGroup>

            {customOpen && (
                <Form onSubmit={applyCustom} noValidate>
                    <Row className="g-2 align-items-center">
                        <Col xs="auto">
                            <Form.Control
                                type="date"
                                aria-label="From date"
                                value={custom.from}
                                max={custom.to || undefined}
                                onChange={(e) => setCustom({ ...custom, from: e.target.value })}
                                isInvalid={Boolean(customError)}
                            />
                        </Col>
                        <Col xs="auto">to</Col>
                        <Col xs="auto">
                            <Form.Control
                                type="date"
                                aria-label="To date"
                                value={custom.to}
                                min={custom.from || undefined}
                                onChange={(e) => setCustom({ ...custom, to: e.target.value })}
                                isInvalid={Boolean(customError)}
                            />
                        </Col>
                        <Col xs="auto">
                            <Button type="submit" variant="primary">Apply</Button>
                        </Col>
                    </Row>
                    {customError && <div className="text-danger small mt-1">{customError}</div>}
                </Form>
            )}
        </div>
    );
};

DateRangeBar.propTypes = {
    range: PropTypes.shape({
        from: PropTypes.string.isRequired,
        to: PropTypes.string.isRequired,
        preset: PropTypes.number
    }).isRequired,
    onChange: PropTypes.func.isRequired
};

export default DateRangeBar;
